import React, { useState } from 'react';
import type { SafeUser, Company } from '../../types/auth';

export interface UserSaveData {
  username: string;
  password: string;
  role: 'admin' | 'user';
  companyId: string | null;
  displayName: string;
}

interface Props {
  initial?: SafeUser;
  companies: Company[];
  onSave: (data: UserSaveData) => Promise<void>;
  onCancel: () => void;
}

export default function UserEditor({ initial, companies, onSave, onCancel }: Props) {
  const [username, setUsername] = useState(initial?.username ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>(initial?.role ?? 'user');
  const [companyId, setCompanyId] = useState(initial?.companyId ?? '');
  const [saving, setSaving] = useState(false);

  const valid = username.trim() && displayName.trim() && (initial || password);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await onSave({ username: username.trim(), password, role, companyId: companyId || null, displayName: displayName.trim() });
    } finally { setSaving(false); }
  }

  return (
    <div className="card border-0 shadow-sm rounded-3" style={{ maxWidth: 720 }}>
      <div className="card-header bg-body py-3">
        <h5 className="mb-0 fw-bold">{initial ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</h5>
        <span className="text-muted small">{initial ? initial.displayName : 'Zugang für die Anrufhistorie anlegen'}</span>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Anzeigename *</label>
              <input className="form-control" value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoFocus required />
              <div className="form-text">Erscheint an Notizen und im Rückruf-Status.</div>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">Benutzername *</label>
              <input className="form-control" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" required />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small fw-semibold">
                Passwort {initial ? <span className="fw-normal text-muted">(leer = unverändert)</span> : '*'}
              </label>
              <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} required={!initial} autoComplete="new-password" />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Rolle</label>
              <select className="form-select" value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'user')}>
                <option value="user">Benutzer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small fw-semibold">Unternehmen</label>
              <select className="form-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value="">— Keins —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {role === 'admin' && (
              <div className="col-12">
                <div className="alert alert-warning py-2 small mb-0">
                  Admins sehen alle Anrufe und verwalten Benutzer, Unternehmen und Gruppen.
                  Anmeldung gilt maximal 8 Stunden, Logout nach 30 Minuten Inaktivität.
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="card-footer bg-body d-flex gap-2 py-3">
          <button type="submit" className="btn btn-primary" disabled={saving || !valid}>{saving ? 'Speichern…' : 'Speichern'}</button>
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}
